import theme from "app/Theme/Theme";
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Dimensions,
  Image,
  Modal,
  Platform,
  Pressable,
  StatusBar,
  StyleSheet,
  View,
} from "react-native";
import FastImage, { Source } from "react-native-fast-image";
import Animated, {
  interpolate,
  runOnJS,
  useAnimatedGestureHandler,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withSpring,
  withTiming,
} from "react-native-reanimated";
import { PressableOpacity } from "../PressableOpacity";
import CrossIcon from "app/components/Icons/CrossIcon";

import {
  Gesture,
  GestureDetector,
  gestureHandlerRootHOC,
  PanGestureHandler,
  PanGestureHandlerGestureEvent,
} from "react-native-gesture-handler";
import {
  isIos,
  isWeb,
  isWebDesktop,
} from "@microdoing/shared/utils/deviceDetectors";
import { useVector, withRubberBandClamp } from "../VideoModal/utils";

let { width, height } = Dimensions.get("window");

const TARGET_WIDTH = isWebDesktop ? 800 : width;

const androidStatusBarOffSetY = isIos ? 0 : StatusBar.currentHeight || 0;

const AnimatedImage = memo(({ source, style, onLoadImage }) => (
  <AnimatedFastImage
    onLoad={onLoadImage}
    style={style}
    source={source}
    resizeMode="cover"
  />
));

const controlAnimateConfig = {
  duration: 150,
};

const CENTER = {
  x: width / 2,
  y: height / 2,
};

const doubleTapInterval = 500;

const maxScale = 6;

const AnimatedFastImage = isWeb
  ? props => <Animated.Image {...props} />
  : Animated.createAnimatedComponent(FastImage);

const styles = StyleSheet.create({
  main: {
    width: "100%",
    position: "relative",
  },
  backdrop: {
    height,
    width,
    position: "absolute",
    marginHorizontal: 0,
    marginBottom: 0,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    zIndex: 3,
    overflow: "hidden",
  },
  backdropTouch: {
    width: "100%",
    height: "100%",
    zIndex: 3,
  },
  controlView: {
    backgroundColor: "rgba(0,0,0,1)",
    justifyContent: "center",
    overflow: "hidden",
    ...StyleSheet.absoluteFillObject,
  },
  slider: {
    left: 0,
    bottom: 0,
  },
  back: {
    width: 16,
    height: 16,
  },
  more: {
    width: 24,
    height: 24,
  },
  exitIconStyle: {
    position: "absolute",
    right: 15,
    top: Platform.OS === "ios" ? 45 : 25,
    zIndex: 6,
  },
  image: {
    borderRadius: 4,
  },
});

interface Props {
  source: Source;
  maxHeight?: number;
  borderRadius?: number;
  controlTimeout?: number;
  hasSpringAnimation?: boolean;
  modalImageStyle?: {};
  style?: {};
}

const ImageModal = memo(
  ({
    source,
    controlTimeout = 2000,
    maxHeight,
    borderRadius = 8,
    modalImageStyle = {},
    hasSpringAnimation = false,
    style,
  }: Props) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);

    const controlViewOpacity = useSharedValue(0);

    const viewRef = useRef(null);

    const originImageOpacity = useSharedValue(1);

    const originImageHeight = useSharedValue(height * 0.3);
    const originImageWidth = useSharedValue(TARGET_WIDTH * 0.6);
    const imageHeight = useSharedValue(maxHeight || height * 0.3);
    const imageWidth = useSharedValue(TARGET_WIDTH * 0.6);

    const scaleRatio = useMemo(() => TARGET_WIDTH / originImageWidth.value, [
      TARGET_WIDTH,
      originImageWidth.value,
    ]);

    const imageRatio = useSharedValue(9 / 16);

    const originXPosition = useSharedValue(0);
    const originYPosition = useSharedValue(0);
    const xPosition = useSharedValue(0);
    const yPosition = useSharedValue(0);

    const x = useSharedValue(0);
    const y = useSharedValue(0);
    const scale = useSharedValue(1);

    /**
     * For pinch to zoom
     */
    const adjustedFocal = useVector(0, 0);
    const translation = useVector(0, 0);
    const origin = useVector(0, height / 2);
    const offset = useVector(0, 0);
    const scaleOffset = useSharedValue(1);
    const zoomScale = useSharedValue(1);

    const setControlTimeout = useCallback(() => {
      "worklet";
      if (isWebDesktop) return;
      controlViewOpacity.value = withDelay(controlTimeout, withTiming(0));
    }, []);

    const setAdjustedFocal = useCallback(
      ({ focalX, focalY }: Record<"focalX" | "focalY", number>) => {
        "worklet";

        adjustedFocal.x.value = focalX - (CENTER.x + offset.x.value);
        adjustedFocal.y.value = focalY - (CENTER.y + offset.y.value);
      },
      [],
    );

    const onStart = useCallback(() => {
      "worklet";

      offset.x.value = offset.x.value + translation.x.value;
      offset.y.value = offset.y.value + translation.y.value;

      translation.x.value = 0;
      translation.y.value = 0;
    }, []);

    const closeImageOnJS = useCallback(() => {
      setTimeout(() => setIsOpen(false), 150);
    }, []);

    const closeImage = useCallback(() => {
      "worklet";
      originImageOpacity.value = 1;

      runOnJS(closeImageOnJS)();
    }, []);

    // useEffect(() => {
    //   if (source) {
    //     closeImage();
    //   }
    // }, [source]);

    const viewStyle = useMemo(() => {
      return {
        zIndex: 5,
      };
    }, []);

    const originImageStyle = useAnimatedStyle(() => {
      return {
        opacity: isIos
          ? interpolate(
              imageWidth.value,
              [originImageWidth.value, originImageWidth.value + 1],
              [1, 0],
            )
          : originImageOpacity.value,
        height: originImageHeight.value,
        zIndex: 2,
      };
    });

    const originImageStyleInside = useAnimatedStyle(() => {
      return {
        opacity: isIos
          ? interpolate(
              imageWidth.value,
              [originImageWidth.value, width],
              [borderRadius, 0],
            )
          : originImageOpacity.value,
        height: originImageHeight.value,
        zIndex: 2,
      };
    });

    const animatedImageStyle = useAnimatedStyle(() => {
      return {
        borderRadius: isIos
          ? interpolate(
              imageWidth.value,
              [originImageWidth.value, width],
              [borderRadius, 0],
            )
          : borderRadius,
        height: imageHeight.value,
        width: imageWidth.value,
        left: xPosition.value,
        top: yPosition.value,
        transform: [
          { translateX: x.value + offset.x.value + translation.x.value },
          { translateY: y.value + offset.y.value + translation.y.value },
          { scale: scale.value * zoomScale.value },
        ],
        zIndex: 6,
        position: "absolute",
      };
    });

    const gestureHandler = useAnimatedGestureHandler<
      PanGestureHandlerGestureEvent,
      {}
    >({
      onStart: (_, context) => {
        if (imageWidth.value === originImageWidth.value) return;
        context.x = 0;
        context.y = 0;
      },
      onActive: (event, context) => {
        if (imageWidth.value === originImageWidth.value) return;
        //Prevent modal to go up more than it should§
        y.value = context.y + event.translationY;
        x.value = context.x + event.translationX;

        if (zoomScale.value === 1) {
          scale.value = interpolate(
            Math.abs(event.translationY),
            [0, height / 2],
            [1, 0.3],
          );
        }

        controlViewOpacity.value = interpolate(
          Math.abs(event.translationY),
          [0, height / 4],
          [1, 0],
        );
      },
      onEnd: (event, context) => {
        if (imageWidth.value === originImageWidth.value) return;
        //Determine if modal should close or go back to its original height
        if (zoomScale.value > 1) {
          controlViewOpacity.value = 1;
          y.value = withSpring(context.y);
          x.value = withTiming(context.x);
        } else if (
          event.velocityY > 0 ||
          Math.abs(event.translationY) + imageHeight.value > height / 2 ||
          Math.abs(event.translationX) > TARGET_WIDTH / 2
        ) {
          x.value = withTiming(0);
          y.value = withTiming(0);

          xPosition.value = withTiming(originXPosition.value);
          yPosition.value = withTiming(originYPosition.value);

          scale.value = withTiming(1);
          zoomScale.value = 1;
          controlViewOpacity.value = 0;

          imageWidth.value = hasSpringAnimation
            ? withSpring(originImageWidth.value)
            : withTiming(originImageWidth.value);
          imageHeight.value = hasSpringAnimation
            ? withSpring(originImageHeight.value, {}, closeImage)
            : withTiming(originImageHeight.value, {}, closeImage);
        } else {
          controlViewOpacity.value = 1;
          y.value = withSpring(context.y);
          x.value = withTiming(context.x);
          scale.value = withTiming(TARGET_WIDTH / imageWidth.value);
        }
      },
    });

    const resetValues = (animated = true) => {
      "worklet";
      zoomScale.value = animated ? withTiming(1) : 0;
      offset.x.value = animated ? withTiming(0) : 0;
      offset.y.value = animated ? withTiming(0) : 0;
      translation.x.value = animated ? withTiming(0) : 0;
      translation.y.value = animated ? withTiming(0) : 0;
      setAdjustedFocal({ focalX: 0, focalY: 0 });
    };

    const pinchGestureHandler = Gesture.Pinch()
      .onStart(({ focalX, focalY }) => {
        onStart();
        scaleOffset.value = scale.value;
        setAdjustedFocal({ focalX, focalY });
        origin.x.value = adjustedFocal.x.value;
        origin.y.value = adjustedFocal.y.value;
      })
      .onUpdate(({ scale: s, focalX, focalY, numberOfPointers }) => {
        if (numberOfPointers !== 2) return;
        if (s * scaleOffset.value < 1) return;

        const nextScale = withRubberBandClamp(
          s * scaleOffset.value,
          0.55,
          maxScale,
          [1, maxScale],
        );

        setAdjustedFocal({ focalX, focalY });

        translation.x.value =
          adjustedFocal.x.value +
          ((-1 * nextScale) / scaleOffset.value) * origin.x.value;
        translation.y.value =
          adjustedFocal.y.value +
          ((-1 * nextScale) / scaleOffset.value) * origin.y.value;

        zoomScale.value = nextScale;
      })
      .onEnd(() => {
        return resetValues();
      });

    const singleTapHandle = useMemo(
      () =>
        Gesture.Tap()
          .numberOfTaps(1)
          .onStart(() => {
            "worklet";
            controlViewOpacity.value = withTiming(1);
            setControlTimeout();
          }),
      [],
    );

    const doubleTapHandle = useMemo(
      () =>
        Gesture.Tap()
          .numberOfTaps(2)
          .maxDuration(doubleTapInterval)
          .onEnd(({ x, y, numberOfPointers }, success) => {
            if (success) {
              if (zoomScale.value === 1) {
                zoomScale.value = withTiming(2);
                const beforeScale = scale.value;
                const actualScale = 2 * beforeScale;
                const diffScale = actualScale - beforeScale;

                translation.x.value = withTiming(
                  ((TARGET_WIDTH / 2 - x) * diffScale) / actualScale,
                );

                translation.y.value = withTiming(
                  ((height / 2 - y) * diffScale) / actualScale,
                );
              } else {
                zoomScale.value = withTiming(1);
                translation.x.value = withTiming(0);
                translation.y.value = withTiming(0);
              }
              if (numberOfPointers !== 1) {
                return;
              }
            }
          }),
      [zoomScale, translation],
    );

    const handlePressImage = useCallback(() => {
      if (isOpen) return;
      if (!isLoaded) return;

      viewRef?.current?.measure((x, y, w, h, px, py) => {
        xPosition.value = px;
        yPosition.value = py;

        setIsOpen(true);
        originImageOpacity.value = 0;

        originXPosition.value = px;
        originYPosition.value = py + androidStatusBarOffSetY;

        const scaleRatio = TARGET_WIDTH / imageWidth.value;

        if (imageRatio.value < 1 && isWebDesktop) {
          // width < height
          imageHeight.value = withTiming(height);
          imageWidth.value = withTiming(height * imageRatio.value);
          xPosition.value = withTiming((width - height * imageRatio.value) / 2);
          yPosition.value = withTiming(0, {}, setControlTimeout);
        } else {
          imageHeight.value = withTiming(imageHeight.value * scaleRatio);
          imageWidth.value = withTiming(TARGET_WIDTH);
          xPosition.value = withTiming((width - TARGET_WIDTH) / 2);
          yPosition.value = withTiming(
            (height - imageHeight.value * scaleRatio) / 2,
            {},
            setControlTimeout,
          );
        }

        controlViewOpacity.value = withTiming(1);
      });
    }, [isLoaded]);

    const handleExit = useCallback(() => {
      "worklet";
      zoomScale.value = 1;
      translation.x.value = 0;
      translation.y.value = 0;
      x.value = withTiming(0);
      y.value = withTiming(0);

      xPosition.value = withTiming(originXPosition.value);
      yPosition.value = withTiming(originYPosition.value);

      scale.value = withTiming(1);
      controlViewOpacity.value = 0;

      originImageOpacity.value = withTiming(1);
      imageWidth.value = withTiming(originImageWidth.value);
      imageHeight.value = withTiming(originImageHeight.value, {}, closeImage);
    }, []);

    const backdropAnimatedStyle = useAnimatedStyle(() => {
      return {
        position: "absolute",
        flex: 1,
        zIndex: 3,
        opacity: interpolate(
          imageHeight.value * scale.value,
          [
            originImageHeight.value * scale.value,
            originImageHeight.value * scaleRatio,
          ],
          [0, 1],
        ),
        backgroundColor: "black",
        //don't show backdrop component if modal is not present, as it cancels any touch events
        top:
          isWebDesktop ||
          imageWidth.value > originImageWidth.value ||
          imageHeight.value > originImageHeight.value
            ? 0
            : height,
        height:
          isIos || isWebDesktop
            ? height
            : height + androidStatusBarOffSetY + height / 2,
      };
    });

    const onPressBackDrop = useCallback(() => {
      if (isWebDesktop) {
        return handleExit();
      }
      controlViewOpacity.value = withTiming(1, controlAnimateConfig);
      setControlTimeout();
    }, []);

    const taps = useMemo(
      () =>
        Gesture.Race(
          Gesture.Exclusive(doubleTapHandle, singleTapHandle),
          pinchGestureHandler,
        ),
      [doubleTapHandle, singleTapHandle, pinchGestureHandler],
    );

    const onLoadImage = useCallback(e => {
      if (isWeb) {
        Image.getSize(source?.uri, (width, height) => {
          if (maxHeight) {
            imageHeight.value = maxHeight;
            originImageHeight.value = maxHeight;
          } else {
            imageHeight.value = withTiming((height / width) * imageWidth.value);
            originImageHeight.value = withTiming(
              (height / width) * imageWidth.value,
            );
          }
          imageRatio.value = width / height;
          setIsLoaded(true);
        });
        return;
      }

      const nativeEvent = e?.nativeEvent;
      const { width, height } = nativeEvent || {};

      if (maxHeight) {
        imageHeight.value = withTiming(maxHeight);
        originImageHeight.value = withTiming(maxHeight);
      } else {
        imageHeight.value = withTiming((height / width) * imageWidth.value);
        originImageHeight.value = withTiming(
          (height / width) * imageWidth.value,
        );
      }
      imageRatio.value = width / height;
      setIsLoaded(true);
    }, []);

    const exitIconAnimatedStyle = useAnimatedStyle(() => {
      return {
        opacity: withTiming(
          interpolate(controlViewOpacity.value, [0, 1], [0, 1]),
        ),
        transform: [
          {
            translateY: withTiming(
              interpolate(controlViewOpacity.value, [0, 1], [-42, 0]),
            ),
          },
        ],
      };
    });

    const onLayout = useCallback(() => {
      viewRef?.current?.measure((x, y, widthImage, height, px, py) => {
        imageWidth.value = withTiming(widthImage);
        originImageWidth.value = withTiming(widthImage);
      });
    }, []);

    const style1 = useMemo(() => {
      return [styles.main, modalImageStyle];
    }, []);

    const style2 = useMemo(() => {
      return [style, originImageStyle];
    }, []);

    const style3 = useMemo(() => {
      return [styles.image, style, originImageStyleInside];
    }, []);

    return (
      <View style={style1}>
        <PressableOpacity onPress={handlePressImage}>
          <Animated.View style={style2} ref={viewRef} onLayout={onLayout}>
            <AnimatedImage
              onLoadImage={onLoadImage}
              style={style3}
              source={source}
            />
          </Animated.View>
        </PressableOpacity>
        {isOpen && (
          <Modal
            onRequestClose={handleExit}
            hardwareAccelerated={true}
            animationType="none"
            visible={isOpen}
            transparent={true}
            statusBarTranslucent
            supportedOrientations={[
              "portrait",
              "portrait-upside-down",
              "landscape",
              "landscape-left",
              "landscape-right",
            ]}
          >
            <ImageInModal
              {...{
                taps,
                source,
                animatedImageStyle,
                gestureHandler,
                onPressBackDrop,
                backdropAnimatedStyle,
                exitIconAnimatedStyle,
                handleExit,
                viewStyle,
              }}
            />
          </Modal>
        )}
      </View>
    );
  },
);

const ImageInModal = isWebDesktop
  ? ({
      gestureHandler,
      taps,
      animatedImageStyle,
      source,
      onPressBackDrop,
      backdropAnimatedStyle,
      exitIconAnimatedStyle,
      handleExit,
      viewStyle,
    }) => (
      <>
        <PanGestureHandler onGestureEvent={gestureHandler}>
          <Animated.View style={viewStyle}>
            <GestureDetector gesture={taps}>
              <AnimatedFastImage
                style={animatedImageStyle}
                source={source}
                resizeMode={isWebDesktop ? "contain" : "cover"}
              />
            </GestureDetector>
          </Animated.View>
        </PanGestureHandler>
        <Pressable onPress={onPressBackDrop}>
          <Animated.View style={[styles.backdrop, backdropAnimatedStyle]}>
            <View style={styles.backdropTouch} />
          </Animated.View>
        </Pressable>
        <Animated.View style={[styles.exitIconStyle, exitIconAnimatedStyle]}>
          <PressableOpacity onPress={handleExit} hitSlop={theme.hitSlop}>
            <CrossIcon color="white" />
          </PressableOpacity>
        </Animated.View>
      </>
    )
  : memo(
      gestureHandlerRootHOC(
        ({
          gestureHandler,
          taps,
          animatedImageStyle,
          source,
          onPressBackDrop,
          backdropAnimatedStyle,
          exitIconAnimatedStyle,
          handleExit,
          viewStyle,
        }) => (
          <>
            <PanGestureHandler onGestureEvent={gestureHandler}>
              <Animated.View style={viewStyle}>
                <GestureDetector gesture={taps}>
                  <AnimatedFastImage
                    style={animatedImageStyle}
                    source={source}
                    resizeMode={isWebDesktop ? "contain" : "cover"}
                  />
                </GestureDetector>
              </Animated.View>
            </PanGestureHandler>
            <Animated.View style={[styles.backdrop, backdropAnimatedStyle]}>
              <Pressable onPress={onPressBackDrop}>
                <View style={styles.backdropTouch} />
              </Pressable>
            </Animated.View>
            <Animated.View
              style={[styles.exitIconStyle, exitIconAnimatedStyle]}
            >
              <PressableOpacity onPress={handleExit} hitSlop={theme.hitSlop}>
                <CrossIcon color="white" />
              </PressableOpacity>
            </Animated.View>
          </>
        ),
      ),
    );

export { ImageModal };
